import { Popover, PopoverContent } from "@/components/ui/popover";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { TableFilterButton } from "@/lib/Components/DataTable/Filters/TableFilterButton";
import { supplierListQuery } from "@/app/Suppliers/GraphQL/supplierListQuery";
import { supplierNameQuery } from "@/app/Suppliers/GraphQL/supplierNameQuery";
import { SupplierIcon } from "@/app/Suppliers/Components/SupplierIcon";

export function SupplierFilter({
  label,
  onChange,
  selected,
}: {
  label: string;
  selected: string | null;
  onChange: (selected: string | null) => void;
}) {
  const [search, setSearch] = useState("");
  const { data } = useGqlQuery(
    supplierListQuery,
    {
      first: 20,
      page: 1,
      search: search || undefined,
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  const { data: selectedData } = useGqlQuery(
    supplierNameQuery,
    { id: selected ?? "" },
    { enabled: Boolean(selected) },
  );

  const selectedLabel = selected ? selectedData?.supplier.name : undefined;

  return (
    <Popover>
      <TableFilterButton
        label={label}
        selectedLabel={selectedLabel}
        numSelected={selected ? 1 : 0}
        onClear={() => onChange(null)}
      />
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search suppliers..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No suppliers found.</CommandEmpty>
            <CommandGroup>
              {data?.suppliers.data.map((supplier) => {
                const isSelected = selected === supplier.id;

                return (
                  <CommandItem
                    key={supplier.id}
                    value={supplier.id}
                    onSelect={(currentValue) => {
                      onChange(selected === currentValue ? null : currentValue);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <div className="flex items-center gap-2">
                      <SupplierIcon className="size-6" model={supplier} />
                      <p>{supplier.name}</p>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
